import { Vec3 } from 'playcanvas';

import type { Camera, CameraFrame } from './camera';
import {
    ProgressTracker,
    type TargetSource,
    clampTurnStep,
    getYawDiffToTarget,
    smoothTurnRate
} from './target-navigation';

/** XZ distance below which the walker considers itself arrived */
const ARRIVAL_DIST = 0.5;

/** Minimum XZ speed (m/s) to not count as blocked */
const BLOCKED_SPEED = 0.6;

/** Seconds of continuous low-progress before stopping the walk */
const BLOCKED_DURATION = 0.2;

/**
 * Generates synthetic move/rotate input to auto-walk toward a target position.
 *
 * Designed to feed into WalkController's existing update path so there is no
 * duplicated physics. Each frame it appends yaw-rotation and forward-movement
 * deltas to the shared CameraFrame, and monitors arrival / blocked conditions.
 */
class WalkSource implements TargetSource {
    /**
     * Forward input scale (matches InputController.moveSpeed for consistent
     * speed with regular WASD walking).
     */
    walkSpeed = 4;

    /**
     * Maximum yaw turn rate in degrees per second.
     */
    maxTurnRate = 192;

    /**
     * Proportional gain mapping yaw error (degrees) to desired turn rate.
     * Below maxTurnRate / turnGain degrees the turn rate scales linearly;
     * above that it is capped at maxTurnRate. The rate filter is
     * automatically critically damped so there is no overshoot.
     */
    turnGain = 5;

    /**
     * Callback fired when an auto-walk completes (arrival or obstacle).
     */
    onComplete: (() => void) | null = null;

    /**
     * 第三人称 Avatar 模式：直接朝目标的世界方向输出 move，由 WalkController
     * 根据移动方向平滑驱动角色 yaw（不再用 rotate delta，因为那只会转相机）。
     */
    avatarMode = false;

    private _target: Vec3 | null = null;

    private _yawRate = 0;

    private _progress = new ProgressTracker();

    private _speedMul = 1;

    get isActive(): boolean {
        return this._target !== null;
    }

    /**
     * Begin auto-walking toward a world-space target position.
     *
     * @param target - The destination (XZ used for navigation).
     * @param speedMul - Forward-speed multiplier (mirrors gaming-controls
     * shift/ctrl: 2 for run, 0.5 for crawl). Defaults to 1.
     */
    navigateTo(target: Vec3, speedMul = 1) {
        if (!this._target) {
            this._target = new Vec3();
        }
        this._target.copy(target);
        this._speedMul = speedMul;
        this._progress.reset();
    }

    /**
     * Cancel any active auto-walk.
     */
    cancel() {
        if (this._target) {
            this._target = null;
            this._yawRate = 0;
            this._progress.reset();
            this.onComplete?.();
        }
    }

    /**
     * Compute walk deltas and append them to the frame. Must be called
     * before* the camera controller reads the frame.
     *
     * @param dt - Frame delta time in seconds.
     * @param camera - The current camera state (read-only).
     * @param frame - The shared CameraFrame to append deltas to.
     */
    update(dt: number, camera: Camera, frame: CameraFrame) {
        if (!this._target) return;

        const target = this._target;
        const cameraPosition = camera.position;
        const cameraAngles = camera.angles;

        const dx = target.x - cameraPosition.x;
        const dz = target.z - cameraPosition.z;
        const xzDist = Math.sqrt(dx * dx + dz * dz);

        // arrival
        if (xzDist < ARRIVAL_DIST) {
            this.cancel();
            return;
        }

        // blocked detection: compare with previous frame's distance
        if (this._progress.update(xzDist, dt, BLOCKED_SPEED, BLOCKED_DURATION)) {
            this.cancel();
            return;
        }

        const stepLen = this.walkSpeed * this._speedMul * dt;

        if (this.avatarMode) {
            // 第三人称：rotate delta 改的是相机 yaw 而非角色 yaw，不能用于转身。
            // 改为把"朝目标的世界方向"投影到当前 yaw 基底，作为 move 输入；
            // WalkController 看到非零 move 后会按移动方向平滑驱动 _avatarYaw。
            const invDist = 1 / xzDist;
            const wx = dx * invDist;
            const wz = dz * invDist;
            // PlayCanvas yaw 约定：yaw=0 时 forward=-Z, right=+X。
            const yawRad = cameraAngles.y * Math.PI / 180;
            const sinY = Math.sin(yawRad);
            const cosY = Math.cos(yawRad);
            // forward = (-sinY, 0, -cosY), right = (cosY, 0, -sinY)
            const mx = wx * cosY + wz * (-sinY); // dot with right
            const mz = wx * (-sinY) + wz * (-cosY); // dot with forward
            frame.deltas.move.append([mx * stepLen, 0, mz * stepLen]);
            return;
        }

        // 第一人称：相机 yaw 即角色朝向，通过 rotate delta 边转边走。
        const yawDiff = getYawDiffToTarget(dx, dz, cameraAngles.y);
        this._yawRate = smoothTurnRate(this._yawRate, yawDiff, this.maxTurnRate, this.turnGain, dt);
        const yawStep = clampTurnStep(this._yawRate, yawDiff, dt);
        this._yawRate = dt > 0 ? yawStep / dt : this._yawRate;

        // WalkController applies: _angles.y += -rotate[0]
        frame.deltas.rotate.append([-yawStep, 0, 0]);

        // scale forward speed by alignment: turn in place first, then accelerate
        const alignment = Math.max(0, Math.cos(yawDiff * Math.PI / 180));
        frame.deltas.move.append([0, 0, stepLen * alignment]);
    }
}

export { WalkSource };
